/*
  Kozie — the segmented progress bar under the product photo on mobile.

  Replaces Dawn's prev arrow, next arrow and "1 / 3" counter with a slim bar
  that runs the full width of the photo, split into one segment per photo,
  with the current segment filled. Two photos = two halves, one filled.

  Three deliberate choices:

  · The segments are counted here, in the browser, from the slides actually on
    screen — not printed by Liquid from product.media.size. Hiding variant
    images and switching variants both change how many slides there are, and a
    server-rendered count goes stale the moment they do.

  · The fill is ONE element that slides, not N elements that light up. It
    tracks the scroll position continuously, so it moves with the thumb during
    a swipe instead of jumping when the swipe ends.

  · The class that hides Dawn's arrows is added here, not in the Liquid. If
    this script never runs, the arrows stay and the gallery still works.

  Each segment is also a button: tapping one jumps to that photo, so the bar
  does everything the arrows did and everything dots would have done.

  A product with one photo gets no bar — a single full-width segment says
  nothing, and 8 products in the catalogue have exactly one photo.
*/
/* Scoped in an IIFE deliberately.

   A quick-add pop-up injects the product page's own <script> tags, so this file
   executes a SECOND time whenever a shopper opens quick-add — and quick-add is
   now on every card on every collection page. A top-level `class` declaration
   goes into the shared global lexical environment, so the second execution threw

     SyntaxError: Identifier 'KozieGalleryProgress' has already been declared

   before it ever reached the customElements.get guard below. Reproduced on the
   live preview: one script tag before opening quick-add, two after, one uncaught
   SyntaxError each time. Nothing visibly broke — the pop-up still opened and the
   progress bar still worked — but it is an uncaught error on the most-used
   interaction on the site.

   Function scope makes the redeclaration legal; the define guard, which was
   already here and already correct, stops the second registration. */
(function () {
class KozieGalleryProgress extends HTMLElement {
  connectedCallback() {
    this.sliderComponent = this.closest('media-gallery')?.querySelector('slider-component');
    this.slider = this.sliderComponent?.querySelector('[id^="Slider-Gallery-"]');
    if (!this.slider) return;

    // A quick-add pop-up renders a SECOND gallery on top of whatever page is
    // already open, and on a product page that page has a gallery of its own.
    // `kozie-gallery--progress` goes on <html> and hides Dawn's arrows site
    // wide, so a pop-up instance must never set or clear it: opening a pop-up
    // for a one-photo product would otherwise clear the class and hand the
    // product page underneath its arrows back mid-visit. The pop-up does not
    // need the class — quick-add.css hides Dawn's arrows there already.
    this.isQuickAdd = Boolean(this.closest('quick-add-modal'));

    this.build = this.build.bind(this);
    this.sync = this.sync.bind(this);

    this.build();

    this.slider.addEventListener('scroll', this.sync, { passive: true });

    // Widths change on rotate; the ResizeObserver catches that.
    this.resizeObserver = new ResizeObserver(this.build);
    this.resizeObserver.observe(this.slider);

    // The slide COUNT changes when a variant is chosen and the theme swaps
    // media in place — which does not necessarily change the slider's box, so
    // the ResizeObserver alone can miss it and leave a six-segment bar on a
    // two-photo variant.
    this.mutationObserver = new MutationObserver(this.build);
    this.mutationObserver.observe(this.slider, { childList: true });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.slider?.removeEventListener('scroll', this.sync);
  }

  get slides() {
    return Array.from(this.slider.querySelectorAll('[id^="Slide-"]')).filter((el) => el.clientWidth > 0);
  }

  build() {
    const count = this.slides.length;

    if (count < 2) {
      this.innerHTML = '';
      this.hidden = true;
      this.count = 0;
      this.fill = null;
      this.hits = null;
      if (!this.isQuickAdd) document.documentElement.classList.remove('kozie-gallery--progress');
      return;
    }

    this.hidden = false;
    if (!this.isQuickAdd) document.documentElement.classList.add('kozie-gallery--progress');

    if (this.count !== count) {
      this.count = count;
      this.innerHTML =
        '<div class="kozie-gallery__track" aria-hidden="true"><span class="kozie-gallery__fill"></span></div>' +
        '<div class="kozie-gallery__hits"></div>';
      this.fill = this.querySelector('.kozie-gallery__fill');
      this.fill.style.width = `${100 / count}%`;

      // One button per segment, so a tap on the third segment shows the third
      // photo. They are transparent and sit over the whole row, not just the
      // 3px bar, because a 3px control cannot be hit with a thumb.
      this.hits = this.querySelector('.kozie-gallery__hits');
      const label = this.dataset.label || 'Photo';
      for (let i = 0; i < count; i += 1) {
        const hit = document.createElement('button');
        hit.type = 'button';
        hit.className = 'kozie-gallery__hit';
        hit.setAttribute('aria-label', `${label} ${i + 1} of ${count}`);
        hit.addEventListener('click', () => this.goTo(i));
        this.hits.appendChild(hit);
      }

      // Announced to screen readers, which get no benefit from the bar itself.
      this.setAttribute('role', 'status');
      this.setAttribute('aria-live', 'polite');
    }

    this.sync();
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;

    const target = slide.offsetLeft - this.slider.offsetLeft;
    const from = this.slider.scrollLeft;
    this.slider.scrollTo({ left: target, behavior: 'smooth' });

    /* Same guard as the rail's arrows, for the same reason: where smooth
       scrolling is unavailable this call moves nothing, and tapping a segment
       (or a dot, in the quick-add pop-up) would silently do nothing. Only fires
       when the scroller has not moved AT ALL, so a scroll that landed short, or
       one the shopper took over with a finger, is left alone. */
    setTimeout(() => {
      if (this.slider.scrollLeft === from) this.slider.scrollTo({ left: target, behavior: 'auto' });
    }, 260);
  }

  sync() {
    if (!this.fill || !this.count) return;

    // How far through the strip we are, 0 to 1, read continuously so the fill
    // travels with the swipe rather than snapping at the end of it.
    const travel = this.slider.scrollWidth - this.slider.clientWidth;
    const ratio = travel > 0 ? Math.min(Math.max(this.slider.scrollLeft / travel, 0), 1) : 0;

    // The fill is one segment wide, so it moves across (count - 1) segments.
    this.fill.style.transform = `translateX(${ratio * (this.count - 1) * 100}%)`;

    const current = Math.round(ratio * (this.count - 1)) + 1;
    if (current !== this.announced) {
      this.announced = current;
      this.setAttribute('aria-label', `${this.dataset.label || 'Photo'} ${current} / ${this.count}`);
      if (this.hits) {
        Array.from(this.hits.children).forEach((hit, i) => {
          if (i === current - 1) hit.setAttribute('aria-current', 'true');
          else hit.removeAttribute('aria-current');
        });
      }
    }
  }
}

if (!customElements.get('kozie-gallery-progress')) {
  customElements.define('kozie-gallery-progress', KozieGalleryProgress);
}
})();
